import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useMediaPreferences } from "../hooks/useMediaPreferences";
import {
  DEFAULT_CAMERA_POSITION,
  getDefaultCameraTarget,
} from "./cameraComposition";
import type { TourPose } from "./tour";

const DEFAULT_POSITION = new Vector3(...DEFAULT_CAMERA_POSITION);
const DEFAULT_COMPOSITION_EPSILON = 1e-6;
const TOUR_DAMPING_RATE = 2.6;

export function CameraRig({
  cameraResetToken,
  tourPose = null,
}: {
  readonly cameraResetToken: number;
  readonly tourPose?: TourPose | null;
}) {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const { prefersReducedMotion } = useMediaPreferences();
  const defaultTarget = useMemo(
    () => new Vector3(...getDefaultCameraTarget(size.width / size.height)),
    [size.height, size.width],
  );
  const defaultTargetRef = useRef(defaultTarget);
  const previousDefaultTarget = useRef<Vector3 | null>(null);
  const controls = useMemo(() => {
    const nextControls = new OrbitControls(camera, gl.domElement);
    nextControls.enablePan = false;
    nextControls.autoRotate = false;
    nextControls.enableDamping = true;
    nextControls.dampingFactor = 0.075;
    nextControls.minDistance = 9;
    nextControls.maxDistance = 24;
    nextControls.minPolarAngle = (25 * Math.PI) / 180;
    nextControls.maxPolarAngle = (145 * Math.PI) / 180;
    return nextControls;
  }, [camera, gl.domElement]);
  const controlsRef = useRef(controls);

  useEffect(() => () => controls.dispose(), [controls]);

  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);

  useEffect(() => {
    const activeControls = controlsRef.current;
    const enableDamping = !prefersReducedMotion;
    if (activeControls.enableDamping !== enableDamping) {
      activeControls.enableDamping = enableDamping;
      activeControls.update();
    }
  }, [controls, prefersReducedMotion]);

  useEffect(() => {
    defaultTargetRef.current = defaultTarget;
  }, [defaultTarget]);

  useEffect(() => {
    const target = defaultTargetRef.current;
    camera.position.copy(DEFAULT_POSITION);
    controls.target.copy(target);
    camera.lookAt(target);
    controls.update();
  }, [camera, cameraResetToken, controls]);

  useEffect(() => {
    const previousTarget = previousDefaultTarget.current ?? defaultTarget;
    const wasAtDefault =
      camera.position.distanceTo(DEFAULT_POSITION) <
        DEFAULT_COMPOSITION_EPSILON &&
      controls.target.distanceTo(previousTarget) < DEFAULT_COMPOSITION_EPSILON;

    previousDefaultTarget.current = defaultTarget.clone();

    if (wasAtDefault) {
      controls.target.copy(defaultTarget);
      camera.lookAt(defaultTarget);
      controls.update();
    }
  }, [camera, controls, defaultTarget]);

  useEffect(() => {
    controls.enabled = tourPose === null;
    if (tourPose !== null && prefersReducedMotion) {
      camera.position.set(...tourPose.position);
      controls.target.set(...tourPose.target);
      camera.lookAt(controls.target);
    }
  }, [camera, controls, prefersReducedMotion, tourPose]);

  useFrame((_, delta) => {
    if (tourPose === null) {
      controls.update();
      return;
    }

    const attraction = 1 - Math.exp(-TOUR_DAMPING_RATE * delta);
    camera.position.lerp(
      new Vector3(...tourPose.position),
      prefersReducedMotion ? 1 : attraction,
    );
    controls.target.lerp(
      new Vector3(...tourPose.target),
      prefersReducedMotion ? 1 : attraction,
    );
    camera.lookAt(controls.target);
  });

  return null;
}
